<template>
  <!-- 박스 입고자료 촬영 페이지(2026-10-16 杨英文 #56) — 박스 라벨의 «箱号 QR» 를 폰 카메라로 찍으면 열린다. 로그인 없이 QR 안의 서명으로만 동작.
       사진을 여러 장 올리고 «这个箱子完成» → 다음 박스(다시 QR 스캔) → 마지막에 «全部完成» -->
  <div class="bpm">
    <div v-if="loading" class="bpm__state">加载中...<small>(불러오는 중)</small></div>
    <div v-else-if="fatal" class="bpm__state bpm__state--err">{{ fatal }}</div>

    <!-- 전체 완료 -->
    <div v-else-if="allDone" class="bpm__state bpm__done">
      <div class="bpm__done-title">✅ 入库资料拍摄上传完成</div>
      <div class="bpm__done-ko">입고자료 촬영·업로드 완료</div>
      <div class="bpm__session">
        <div v-for="b in session" :key="b.labelId" class="bpm__session-row">📦 {{ b.labelId }} <small>· {{ b.count }}张</small></div>
      </div>
      <button type="button" class="bpm__btn" @click="allDone = false">返回<small>(돌아가기)</small></button>
    </div>

    <template v-else-if="box">
      <div class="bpm__head">
        <div class="bpm__no">📦 {{ box.labelId }}</div>
        <div class="bpm__meta">{{ box.화주 }}<span v-if="box.박스크기"> · {{ box.박스크기 }}</span></div>
        <div v-if="box.완료" class="bpm__ok">✅ 已完成<small>(완료)</small> · 可继续补充照片<small>(사진 추가 가능)</small></div>
      </div>

      <div class="bpm__actions">
        <button type="button" class="bpm__btn bpm__btn--main" @click="$refs.cam.click()">📷 拍照<small>(촬영)</small></button>
        <button type="button" class="bpm__btn" @click="$refs.album.click()">🖼 相册<small>(앨범, 여러 장)</small></button>
        <input ref="cam" type="file" accept="image/*" capture="environment" style="display:none" @change="onFiles">
        <input ref="album" type="file" accept="image/*" multiple style="display:none" @change="onFiles">
      </div>

      <div v-if="busy" class="bpm__msg">上传中<small>(업로드 중)</small> {{ busy }}张...</div>
      <div v-if="message" class="bpm__msg" :class="{ 'bpm__msg--err': messageErr }">{{ message }}</div>
      <div v-if="reading" class="bpm__msg">🔎 正在识别入库资料...<small>(입고자료 판독 중 — 잠시만)</small></div>
      <div v-else-if="readInfo" class="bpm__msg" :class="{ 'bpm__msg--err': !readInfo.ok }">
        <template v-if="readInfo.ok">🔎 识别结果<small>(판독)</small> — 入库ID {{ readInfo.id || '-' }} · 入库预定日 {{ readInfo.date || '-' }} · 入库中心 {{ readInfo.center || '-' }}</template>
        <template v-else>未能自动识别，请在电脑上手动填写<small>(자동 판독 실패 — PC 의 Coupang入库资料 창에서 직접 입력)</small></template>
      </div>

      <div class="bpm__title">已上传<small>(업로드된 사진)</small> {{ box.사진.length }}张</div>
      <div class="bpm__grid">
        <div v-for="p in box.사진" :key="p.url" class="bpm__cell">
          <a :href="p.url" target="_blank" rel="noopener"><img :src="p.url" alt=""></a>
          <button type="button" class="bpm__del" :disabled="removing === p.url" @click="removePhoto(p)">✕</button>
        </div>
      </div>

      <div class="bpm__footer">
        <button type="button" class="bpm__btn bpm__btn--done" :disabled="!box.사진.length || busy > 0 || finishing" @click="finishBox">
          ✅ 这个箱子完成<small>(이 박스 완료)</small>
        </button>
        <div v-if="!box.사진.length" class="bpm__hint">请先拍摄至少1张照片<small>(사진을 1장 이상 올려 주세요)</small></div>
      </div>

      <!-- 다음 박스 안내 + 이번 작업에서 끝낸 박스 -->
      <div v-if="session.length" class="bpm__next">
        <div class="bpm__next-title">下一个箱子：请用手机相机扫描下一个箱子的二维码<small>(다음 박스: 폰 카메라로 다음 박스 QR 을 스캔)</small></div>
        <div class="bpm__session">
          <div class="bpm__session-title">本次已完成<small>(이번에 끝낸 박스)</small> {{ session.length }}</div>
          <div v-for="b in session" :key="b.labelId" class="bpm__session-row">📦 {{ b.labelId }} <small>· {{ b.count }}张</small></div>
        </div>
        <button type="button" class="bpm__btn bpm__btn--finish" @click="allDone = true">🏁 全部完成<small>(전부 완료)</small></button>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { uploadImportPhoto } from '@/utils/importPhotoS3';

// 이번 작업에서 끝낸 박스 목록 — 박스마다 QR 을 새로 열기 때문에 이 브라우저에 3시간만 기억한다(없어도 동작에는 지장 없다)
const SESSION_KEY = 'boxPhotoSession';
const SESSION_TTL = 3 * 60 * 60 * 1000;

export default {
  name: 'BoxPhotoMobile',
  data() {
    return {
      loading: true, fatal: '', box: null, busy: 0, message: '', messageErr: false, removing: '', finishing: false,
      session: [], allDone: false, reading: false, readInfo: null
    };
  },
  computed: {
    labelDash() { return String(this.$route.params.labelId || ''); },
    token() { return String(this.$route.query.t || ''); },
    auth() { return { labelId: this.labelDash, t: this.token }; }
  },
  mounted() {
    this.loadSession();
    this.load();
  },
  methods: {
    loadSession() {
      try {
        const raw = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
        if (raw && Date.now() - raw.at < SESSION_TTL && Array.isArray(raw.boxes)) this.session = raw.boxes;
      } catch (e) {
        console.error('[boxPhoto] 세션 기록 읽기 실패(없어도 동작함):', e);
      }
    },
    saveSession() {
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ at: Date.now(), boxes: this.session }));
      } catch (e) {
        console.error('[boxPhoto] 세션 기록 저장 실패(없어도 동작함):', e);
      }
    },
    say(msg, err = false) { this.message = msg; this.messageErr = err; },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.post('/api/boxPhoto/info', this.auth);
        if (data.요청결과 === '성공') this.box = data.box;
        else this.fatal = data.error && data.error.errorCode === '401' ? '二维码无效，请重新扫描箱子标签上的二维码 (유효하지 않은 QR — 박스 라벨의 QR 을 다시 스캔하세요)'
          : data.error && data.error.errorCode === '404' ? '找不到这个箱子 (박스를 찾을 수 없습니다)' : '加载失败(불러오기 실패)';
      } catch (e) {
        console.error('[boxPhoto] 불러오기 오류:', e);
        this.fatal = '网络错误，请重试 (네트워크 오류 — 다시 시도)';
      } finally {
        this.loading = false;
      }
    },
    async onFiles(e) {
      const files = Array.from((e.target && e.target.files) || []);
      e.target.value = '';
      if (!files.length) return;
      this.say('');
      this.busy += files.length;
      const urls = [];
      for (const f of files) {
        try {
          urls.push(await uploadImportPhoto(f, 'box-' + this.labelDash));
        } catch (err) {
          console.error('[boxPhoto] 업로드 실패:', err);
          this.say('有照片上传失败，请重试 (일부 사진 업로드 실패 — 다시 시도)', true);
        } finally {
          this.busy -= 1;
        }
      }
      if (!urls.length) return;
      try {
        const { data } = await axios.post('/api/boxPhoto/add', { ...this.auth, urls });
        if (data.요청결과 === '성공') this.box = data.box;
        else this.say('保存失败 (저장 실패): ' + ((data.error && data.error.errorMessage) || ''), true);
      } catch (err) {
        console.error('[boxPhoto] 사진 저장 오류:', err);
        this.say('保存失败，请重试 (저장 실패 — 다시 시도)', true);
      }
    },
    async removePhoto(p) {
      this.removing = p.url;
      try {
        const { data } = await axios.post('/api/boxPhoto/remove', { ...this.auth, url: p.url });
        if (data.요청결과 === '성공') this.box = data.box;
        else this.say('删除失败 (삭제 실패)', true);
      } catch (err) {
        console.error('[boxPhoto] 삭제 오류:', err);
        this.say('删除失败 (삭제 실패)', true);
      } finally {
        this.removing = '';
      }
    },
    // 입고자료 사진을 서버가 읽어 박스의 入库ID·입고예정일·입고센터를 채운다(2026-10-18 杨英文 #56 A안). 실패해도 완료는 그대로
    async readInbound() {
      this.reading = true;
      this.readInfo = null;
      try {
        const { data } = await axios.post('/api/boxPhoto/inbound', this.auth);
        if (data.요청결과 === '성공') {
          const r = data.read;
          this.readInfo = r && (r.id || r.date || r.center) ? { ok: true, id: r.id, date: r.date, center: r.center } : { ok: false };
          if (data.box) this.box = data.box;
        } else {
          this.readInfo = { ok: false };
        }
      } catch (err) {
        console.error('[boxPhoto] 입고자료 판독 오류:', err);
        this.readInfo = { ok: false };
      } finally {
        this.reading = false;
      }
    },
    async finishBox() {
      this.finishing = true;
      try {
        const { data } = await axios.post('/api/boxPhoto/done', this.auth);
        if (data.요청결과 !== '성공') { this.say('完成失败 (완료 실패): ' + ((data.error && data.error.errorMessage) || ''), true); return; }
        this.box = data.box;
        const rest = this.session.filter(b => b.labelId !== this.box.labelId);
        this.session = rest.concat([{ labelId: this.box.labelId, count: this.box.사진.length, at: Date.now() }]);
        this.saveSession();
        this.say('✅ 这个箱子已完成。请扫描下一个箱子的二维码 (이 박스 완료 — 다음 박스 QR 스캔)');
        await this.readInbound();
      } catch (err) {
        console.error('[boxPhoto] 완료 오류:', err);
        this.say('完成失败，请重试 (완료 실패 — 다시 시도)', true);
      } finally {
        this.finishing = false;
      }
    }
  }
};
</script>

<style scoped>
.bpm { max-width: 560px; margin: 0 auto; padding: 12px; font-size: 16px; color: #111; }
.bpm small { margin-left: 4px; font-size: 12px; opacity: .7; }
.bpm__state { text-align: center; padding: 48px 12px; }
.bpm__state--err { color: #b00020; }
.bpm__done-title { font-size: 22px; font-weight: 700; color: #1b7f3b; }
.bpm__done-ko { margin: 4px 0 16px; opacity: .7; }
.bpm__head { background: #f1f5f9; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; }
.bpm__no { font-size: 20px; font-weight: 700; word-break: break-all; }
.bpm__meta { opacity: .75; }
.bpm__ok { margin-top: 4px; color: #1b7f3b; font-weight: 600; }
.bpm__actions { display: flex; gap: 8px; margin: 10px 0; }
.bpm__btn { flex: 1; border: 1px solid #94a3b8; background: #fff; border-radius: 10px; padding: 14px 8px; font-size: 17px; font-weight: 600; }
.bpm__btn--main { background: #2563eb; border-color: #2563eb; color: #fff; }
.bpm__btn--done { width: 100%; background: #16a34a; border-color: #16a34a; color: #fff; padding: 16px; }
.bpm__btn--finish { width: 100%; margin-top: 10px; background: #f59e0b; border-color: #f59e0b; color: #fff; }
.bpm__btn:disabled { opacity: .45; }
.bpm__msg { margin: 8px 0; padding: 8px 10px; border-radius: 8px; background: #ecfdf5; }
.bpm__msg--err { background: #fef2f2; color: #b00020; }
.bpm__title { margin: 12px 0 6px; font-weight: 700; }
.bpm__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.bpm__cell { position: relative; aspect-ratio: 1; background: #e5e7eb; border-radius: 8px; overflow: hidden; }
.bpm__cell img { width: 100%; height: 100%; object-fit: cover; }
.bpm__del { position: absolute; top: 4px; right: 4px; width: 28px; height: 28px; border: 0; border-radius: 50%; background: rgba(0,0,0,.6); color: #fff; }
.bpm__footer { margin: 14px 0; }
.bpm__hint { margin-top: 6px; opacity: .7; text-align: center; }
.bpm__next { margin-top: 16px; padding: 12px; border: 2px dashed #94a3b8; border-radius: 10px; }
.bpm__next-title { font-weight: 700; }
.bpm__session { margin-top: 8px; }
.bpm__session-title { font-weight: 600; margin-bottom: 4px; }
.bpm__session-row { padding: 3px 0; border-top: 1px solid #e5e7eb; word-break: break-all; }
</style>
